/** @jsxImportSource react */
import React, { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../services/api'
import { useTranslation } from '../i18n'
import {
  Bell,
  Search,
  CheckCircle,
  Clock,
  X,
  ChevronRight,
  Filter,
  ArrowDown,
  AlertCircle,
  Mail,
} from 'lucide-react'
import { Notification, User } from '../types'

const NotificationsPage = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all') // 'all', 'unread', 'read'

  const { data: notifications = [], isLoading: isLoadingNotifications } =
    useQuery({
      queryKey: ['notifications'],
      queryFn: () => api.getNotifications(),
      refetchInterval: 30000,
    })

  const { data: users = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers(),
  })

  const userMap = useMemo(() => {
    if (!users) return {}
    return users.reduce(
      (acc, u) => {
        acc[u.id] = u
        return acc
      },
      {} as Record<string, User>,
    )
  }, [users])

  const getSenderName = React.useCallback((senderId?: string) => {
    if (!senderId || senderId === 'system') return t('systemSender')
    return userMap[senderId]?.name || t('unknownSender')
  }, [t, userMap])

  const getSenderAvatar = (senderId?: string) => {
    if (!senderId) return null
    return (userMap[senderId] as any)?.avatar || null // Assuming avatar can be on user
  }

  const getNotificationText = React.useCallback((notification: Notification) => {
    if (!notification.messageKey) return notification.message;
    
    const params = { ...notification.messageParams };
    if (notification.messageKey.includes('orderStatusUpdate') && params.status) {
        params.status = t(String(params.status).toLowerCase());
    }
    
    return t(notification.messageKey, params);
  }, [t]);

  const filteredNotifications = useMemo(() => {
    let filtered = [...notifications]
    // Apply status filter
    if (filterType === 'unread') {
      filtered = filtered.filter((n) => !n.isRead)
    } else if (filterType === 'read') {
      filtered = filtered.filter((n) => n.isRead)
    }
    // Apply search filter
    if (searchTerm) {
      const lowercasedFilter = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (n) =>
          getNotificationText(n).toLowerCase().includes(lowercasedFilter) ||
          getSenderName(n.senderId).toLowerCase().includes(lowercasedFilter),
      )
    }
    return filtered
  }, [searchTerm, notifications, filterType, getSenderName, getNotificationText])

  const unreadCount = notifications.filter((n) => !n.isRead).length

  const markAsReadMutation = useMutation({
    mutationFn: () => api.markNotificationsAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
      })
    },
  })

  const markSingleAsReadMutation = useMutation({
    mutationFn: (id: string) => api.markNotificationAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
      })
    },
  })

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markSingleAsReadMutation.mutate(notification.id)
    }
    if (notification.link) {
      navigate(notification.link)
    }
  }

  const isLoading = isLoadingNotifications || isLoadingUsers

  // Helper for formatted time
  const getFormattedTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 60) {
      return t('min_ago', { count: diffMins });
    } else if (diffHours < 24) {
      return t('h_ago', { count: diffHours });
    } else if (diffDays < 7) {
      return t('d_ago', { count: diffDays });
    } else {
      return date.toLocaleDateString()
    }
  }

  // Get notification type icon
  const getNotificationIcon = (type?: string) => {
    switch (type) {
      case 'order':
        return <Mail className="w-5 h-5 text-primary-500" />
      case 'system':
        return <AlertCircle className="w-5 h-5 text-secondary-500" />
      case 'alert':
        return <Bell className="w-5 h-5 text-amber-500" />
      default:
        return <Bell className="w-5 h-5 text-primary-500" />
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {t('notifications') || 'Notifications'}
          </h1>
          <p className="text-slate-600 max-w-3xl">
            {t('notificationsDesc') ||
              'Stay updated with all your important messages and alerts.'}
          </p>
        </div>

        {/* Search and Actions */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-slate-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={
                  t('searchNotifications') || 'Search notifications...'
                }
                className="block w-full ps-10 pe-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {/* Filter Dropdown */}
              <div className="relative inline-block group">
                <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors">
                  <Filter className="w-4 h-4" />
                  <span>
                    {filterType === 'all' && (t('allNotifications') || 'All')}
                    {filterType === 'unread' && (t('unreadOnly') || 'Unread')}
                    {filterType === 'read' && (t('readOnly') || 'Read')}
                  </span>
                  <ArrowDown className="w-4 h-4" />
                </button>
                <div className="absolute end-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 z-10 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-300">
                  <div className="py-1">
                    <button
                      onClick={() => setFilterType('all')}
                      className="block w-full text-start px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                    >
                      {t('allNotifications') || 'All Notifications'}
                    </button>
                    <button
                      onClick={() => setFilterType('unread')}
                      className="block w-full text-start px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                    >
                      {t('unreadOnly') || 'Unread Only'}
                    </button>
                    <button
                      onClick={() => setFilterType('read')}
                      className="block w-full text-start px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                    >
                      {t('readOnly') || 'Read Only'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Mark All as Read Button */}
              {unreadCount > 0 && (
                <button
                  onClick={() => markAsReadMutation.mutate()}
                  disabled={markAsReadMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>{t('markAllAsRead') || 'Mark All as Read'}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Notification Counter Summary */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex-1 min-w-[200px]">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                <Bell className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">
                  {t('totalNotifications') || 'Total Notifications'}
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {notifications.length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex-1 min-w-[200px]">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">
                  {t('unreadNotifications') || 'Unread Notifications'}
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {unreadCount}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex-1 min-w-[200px]">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">
                  {t('readNotifications') || 'Read Notifications'}
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {notifications.length - unreadCount}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse flex items-start gap-4 pb-4 border-b border-slate-100"
                >
                  <div className="w-10 h-10 bg-slate-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-slate-200 rounded w-1/4 mb-2"></div>
                    <div className="h-3 bg-slate-200 rounded w-full mb-2"></div>
                    <div className="h-3 bg-slate-200 rounded w-3/4"></div>
                  </div>
                  <div className="w-20 h-4 bg-slate-200 rounded"></div>
                </div>
              ))}
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="p-12 text-center">
              <div className="mx-auto h-16 w-16 text-slate-400 mb-4">
                <Bell className="w-full h-full" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-1">
                {t('noNotificationsFound') || 'No notifications found'}
              </h3>
              <p className="text-slate-500 max-w-md mx-auto">
                {searchTerm
                  ? t('noNotificationsMatchSearch') ||
                    'No notifications match your search criteria'
                  : t('noNotificationsYet') ||
                    "You don't have any notifications yet"}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-4 px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  <span>{t('clearSearch') || 'Clear search'}</span>
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredNotifications.map((notification) => {
                const senderName = getSenderName(notification.senderId)
                const senderAvatar = getSenderAvatar(notification.senderId)
                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-5 hover:bg-slate-50 transition-colors cursor-pointer flex items-start gap-4 ${!notification.isRead ? 'bg-blue-50/50' : ''}`}
                  >
                    {/* Avatar/Icon */}
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${!notification.isRead ? 'bg-primary-100' : 'bg-slate-100'}`}
                    >
                      {senderAvatar ? (
                        <img
                          src={senderAvatar}
                          alt={senderName}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : notification.senderId ? (
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${!notification.isRead ? 'bg-primary-100 text-primary-600' : 'bg-slate-100 text-slate-600'} font-semibold text-lg`}
                        >
                          {senderName.charAt(0).toUpperCase()}
                        </div>
                      ) : (
                        <div
                          className={`flex items-center justify-center ${!notification.isRead ? 'text-primary-600' : 'text-slate-500'}`}
                        >
                          {getNotificationIcon((notification as any).type)}
                        </div>
                      )}
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-1">
                        <h4
                          className={`font-medium ${!notification.isRead ? 'text-primary-700' : 'text-slate-700'}`}
                        >
                          {senderName}
                        </h4>
                        <span className="text-xs text-slate-500">
                          {getFormattedTime(notification.createdAt)}
                        </span>
                      </div>
                      <p
                        className={`text-sm ${!notification.isRead ? 'text-slate-900 font-medium' : 'text-slate-600'}`}
                      >
                        {getNotificationText(notification)}
                      </p>
                    </div>
                    {/* Status Indicator */}
                    {!notification.isRead && (
                      <span className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-500 mt-2"></span>
                    )}
                    {/* Action Icon */}
                    {notification.link && (
                      <ChevronRight
                        className={`flex-shrink-0 w-5 h-5 ${
                          !notification.isRead ? 'text-primary-500' : 'text-slate-400'
                        } rtl:rotate-180`}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default NotificationsPage